import React from 'react';
import { Typography } from '@material-tailwind/react';
import {
  type TextSize,
  type TextWeight,
  type TextColor,
  getColorClass,
  getWeightClass,
} from 'src/modules/shared/components/atoms/text/text.styles';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { linkifyTextParts } from 'src/modules/shared/utils/text/linkify.util';

interface TextProps {
  children: React.ReactNode;
  size?: TextSize;
  color?: TextColor;
  weight?: TextWeight;
  className?: string;
  as?: React.ElementType;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  target?: string;
  rel?: string;
  title?: string;
  style?: React.CSSProperties;
  disableAutoLink?: boolean;
  /** Element id, for example the target of an `aria-describedby`. */
  id?: string;
  /** Id of the field this text names, when it renders as a `label`. */
  htmlFor?: string;
}

export const Text: React.FC<TextProps> = ({
  children,
  size = 'paragraph',
  color = 'inherit',
  weight = 'regular',
  className = '',
  as,
  onClick,
  href,
  target,
  rel,
  title,
  style,
  disableAutoLink = false,
  id,
  htmlFor,
}) => {
  const sizeClass = size === 'tiny' ? 'text-xs' : '';
  const variantSize = size === 'tiny' ? undefined : size;
  const shouldAutoLink =
    !disableAutoLink &&
    !href &&
    as !== 'a' &&
    (typeof children === 'string' || typeof children === 'number');
  const content = shouldAutoLink
    ? linkifyTextParts(String(children)).map((part, index) =>
        part.type === 'url' && part.href ? (
          <Link
            key={`${part.href}-${index}`}
            href={part.href}
            size={size}
            weight={weight}
            variant={color === 'inherit' ? 'primary' : color}
            showExternalIcon={false}
            className="break-all"
          >
            {part.value}
          </Link>
        ) : (
          <React.Fragment key={`${part.value}-${index}`}>
            {part.value}
          </React.Fragment>
        )
      )
    : children;
  // Typography types its attributes as a paragraph's, so `htmlFor`, valid
  // only when rendered as a label, goes through a spread.
  const labelAttributes = htmlFor ? { htmlFor } : {};

  return (
    <Typography
      variant={variantSize}
      className={`${getColorClass(color)} ${getWeightClass(weight)} ${sizeClass} ${className}`}
      as={as}
      id={id}
      {...labelAttributes}
      onClick={onClick}
      href={href}
      target={target}
      rel={rel}
      title={title}
      style={style}
    >
      {content}
    </Typography>
  );
};
